import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiGetGuestPaymentTransactionMutation } from '@hooks-api/fortune/use-api-get-guest-payment-transaction-mutation'
import { type PaymentOrderHash } from '@hooks-api/types/dataModel/eCommercePayments'
import { FastpassOrderStatus } from '@hooks-api/types/dataModel/fpOrder'
import { queryStringify } from '@utils'
import { useRef } from 'react'

interface UseFpCampaignPaymentTransactionPollingProps {
  orderHash: PaymentOrderHash
  interval?: number
  basePath: string
}

export const useFpCampaignPaymentTransactionPolling = ({
  orderHash,
  interval = 5000,
  basePath,
}: UseFpCampaignPaymentTransactionPollingProps) => {
  const timerId = useRef<NodeJS.Timeout | null>(null)

  const { trigger: triggerGuestTransaction } = useApiGetGuestPaymentTransactionMutation()

  const pollOrderStatus = useCallbackEvent(async (): Promise<FastpassOrderStatus | undefined> => {
    if (!orderHash) {
      console.error(`[useFpCampaignPaymentTransactionPolling] orderHash: ${orderHash}`)
      return undefined
    }
    try {
      const guestResponse = await triggerGuestTransaction({ orderHash })
      return guestResponse.data.data.status
    }
    catch (error) {
      console.error(`[useFpCampaignPaymentTransactionPolling] ${error}`)
      return undefined
    }
  })

  const stopPolling = useCallbackEvent(() => {
    if (timerId.current) {
      clearInterval(timerId.current)
      timerId.current = null
    }
  })

  const startPolling = useCallbackEvent(async () => {
    timerId.current = setInterval(async () => {
      const status = await pollOrderStatus()
      const completedStatuses = [
        FastpassOrderStatus.COMPLETED,
        FastpassOrderStatus.FAILED,
      ]
      const shouldStop = !!(status && completedStatuses.includes(status))
      if (shouldStop) {
        stopPolling()
      }
      if (status === FastpassOrderStatus.COMPLETED) handleOrderComplete()
      if (status === FastpassOrderStatus.FAILED) handleOrderFailed()
    }, interval)
  })

  const handleOrderComplete = useCallbackEvent(() => {
    window.location.href = `${window.location.origin}${basePath}/thankyou/${orderHash}`
  })

  const handleOrderFailed = useCallbackEvent(() => {
    window.location.href = `${window.location.origin}${basePath}/payments?${queryStringify({ orderHash, paymentError: true })}`
  })

  return {
    startPolling,
    stopPolling,
  }
}
